import type { ChangeEvent } from 'react';
import { Cross2Icon, Pencil1Icon } from '@radix-ui/react-icons';
import { Button, Dialog, Spinner, TextField } from '@radix-ui/themes';
import { useLocation } from 'react-router-dom';
import { updateMatchResult } from 'store/draw/drawSlice';
import { clear, compute } from 'store/simulator/simulatorSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import { saveResult, updateIsPlayed, updateResult } from 'store/updateMatchResult/updateMatchResultSlice';

const UpdateMatchResultModal: React.FC<{
  editMatchResult: () => void;
}> = ({ editMatchResult }) => {
  const { loading, error, id, type, result, player_id, is_played } = useAppSelector((state) => state.updateMatchResult);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isValid = () => result.length <= 17;

  const buttonFromMatches = (
    <Button
      disabled={!isValid()}
      onClick={() => {
        dispatch(
          saveResult({
            id,
            data: { type, result, is_played },
            onCallback: () => {
              dispatch(clear());
              dispatch(compute({ playerId: player_id }));
            },
          }),
        );
      }}
    >
      Salva
    </Button>
  );

  const buttonFromDraw = (
    <Button
      disabled={!isValid()}
      onClick={() => {
        dispatch(
          saveResult({
            id,
            data: { type, result, is_played },
            onCallback: () => {
              dispatch(updateMatchResult({ matchId: id, result }));
            },
          }),
        );
      }}
    >
      Salva
    </Button>
  );

  let content = <Spinner />;
  if (!loading) {
    content = (
      <div className='update-result'>
        <div className='inputs mb-10'>
          <div className='mb-5'>
            <div className='flex'>
              <div className='results'>
                <label>Risultato</label>
                <div>
                  <div className='w-full flex-initial'>
                    <TextField.Root
                      placeholder='Inserisci il risultato'
                      disabled={!is_played}
                      value={result}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        if (new RegExp('^[0-9 ]{0,17}$').test(event.target.value)) {
                          dispatch(updateResult(event.target.value));
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='played-section mt-5'>
                  <input
                    type='checkbox'
                    checked={is_played}
                    onChange={(e) => {
                      dispatch(updateIsPlayed(e.target.checked));
                      if (!e.target.checked) dispatch(updateResult('nd'));
                    }}
                  />
                  <span>Disputata</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isValid() && <p className='text-sm text-red-9'>Errore! Immettere un risultato corretto</p>}
        <div className='buttons text-center'>
          {location.pathname.includes('partite') ? buttonFromMatches : buttonFromDraw}
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog.Root>
        <Dialog.Trigger>
          <Button
            size='2'
            variant='ghost'
            className='flex cursor-pointer items-center self-stretch bg-accent-accent-6'
            onClick={() => editMatchResult()}
          >
            <Pencil1Icon />
          </Button>
        </Dialog.Trigger>
        <Dialog.Content maxWidth='450px'>
          <div className='flex w-full justify-between align-middle'>
            <Dialog.Title>Modifica Risultato</Dialog.Title>
            <Dialog.Close className='mb-4 cursor-pointer'>
              <Cross2Icon height={'24'} width={'24'} />
            </Dialog.Close>
          </div>
          <Dialog.Description className='hidden'>Modifica risultato del match</Dialog.Description>

          <div className='flex justify-center'>
            {error && <div className='my-5 bg-sand-3 text-center text-red-9'>{error}</div>}

            {content}
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
};

export default UpdateMatchResultModal;
